import type { IndicesTabValue } from '@constants/indicesTabs';

export const INDICES_PERIODS = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
} as const;

export type IndicesPeriod = (typeof INDICES_PERIODS)[keyof typeof INDICES_PERIODS];

export type IndicesMarketGroup = 'world' | 'asia';
export type IndicesHtsInterval = 'd' | 'w' | 'm';
export type IndicesChartState = 'loading' | 'ready' | 'no-data' | 'error';
export type IndicesChangeTone = 'positive' | 'negative' | 'neutral';

export interface IndicesFormattedNumber {
  raw: number | string | null | undefined;
  numericValue: number | null;
  text: string;
  isUsable: boolean;
  decimalPlaces: number;
}

export interface IndicesQuoteViewModel {
  id: string;
  sourceIndex: number;
  ric: string;
  marketType: string | null;
  country: string;
  countryLabel: string;
  name: string;
  updatedAtText: string;
  valueText: string;
  changeText: string;
  changeTone: IndicesChangeTone;
  highText: string;
  lowText: string;
  apiDecimalPlaces: number | null;
  quoteDecimalPlaces: number | null;
}

export interface IndexInstrumentViewModel extends IndicesQuoteViewModel {
  chartData: string | null;
  chartState: IndicesChartState;
  decimalPlaces: number;
}

export interface IndicesCountryViewModel {
  country: string;
  countryLabel: string;
  instruments: readonly IndexInstrumentViewModel[];
}


export type IndicesCountryCode =
  | 'us'
  | 'gb'
  | 'fr'
  | 'de'
  | 'in'
  | 'br'
  | 'mx'
  | 'cn'
  | 'hk'
  | 'au';

export interface IndexOhlcPoint {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  turnover: number;
}

export interface IndexInstrumentMock {
  id: string;
  chartRic: string;
  name: string;
  updatedAt: string;
  value: number;
  valueText: string;
  change: number;
  changeText: string;
  highText?: string;
  lowText?: string;
  decimalPlaces: number;
  candles: Record<IndicesPeriod, readonly IndexOhlcPoint[]>;
}

export interface IndicesCountryMock {
  country: IndicesCountryCode;
  label: string;
  instruments: readonly IndexInstrumentMock[];
}

export type IndicesLayoutRow =
  | {
      kind: 'country-grid';
      country: IndicesCountryCode;
      grid: 'us-wide' | 'standard';
    }
  | {
      kind: 'country-columns';
      countries: readonly IndicesCountryCode[];
    };

export interface IndicesTabMockData {
  tab: IndicesTabValue;
  rows: readonly IndicesLayoutRow[];
}

export interface IndicesChart5Setting {
  priceTrackColor: string;
  priceTrackFontColor: string;
  priceTrackThinkness: number;
  priceTrackStyle: string;
  yAsis: boolean;
  backgroundColor: string;
  chartBackgroundColor: string;
  chartLineColor: string;
  chartLineStyle: string;
  toolTipsColor: string;
  crosshairLineColor: string;
  crosshairLineStyle: string;
  crosshairPointColor: string;
  crosshairPointSize: string;
  crosshairFontColor: string;
  preserveYAxisTrailingZeros: boolean;
  autoYAxisLabelWidth: boolean;
}

export interface IndicesChartOption {
  lineColor: string;
  lineFillColor: string;
  lineFillColor2: string;
  lineGradientEnable: boolean;
  lineStyle: string;
  lineThickness: number;
  upColor: string;
  upFillColor: string;
  downColor: string;
  downFillColor: string;
  barColor: string;
  bar3lineColor: string;
  histogramUpColor: string;
  histogramUpFillColor: string;
  histogramDownColor: string;
  histogramDownFillColor: string;
  showTracker: boolean;
}

export interface IndicesChartBundlePayload {
  source: 'labci-chart5';
  theme: 'light';
  ric: string;
  chartData: string;
  periodType: 'hts';
  chartType: 'candlestick';
  period: '1Y';
  interval: 'd' | 'w' | 'm';
  infoData: string;
  overlay: string;
  eventData: Record<string, never>;
  chart5Setting: IndicesChart5Setting;
  chartOption: IndicesChartOption;
  showCompare: false;
  showCrosshair: boolean;
  showMainChart: true;
  showTracker: false;
  showScrollView: boolean;
  showTA: boolean;
  showDrawTool: false;
  drag2Slide: boolean;
  lang: 'ja';
}

export interface IndicesChartBundleReadyMessage {
  source: 'labci-chart5';
  message: 'ready';
}
